import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, HttpError } from "../api/client";
import type { Folder, TeamDocument } from "../api/types";
import { FileTypeIcon } from "../components/FileTypeIcon";
import { fmtBytes, fmtRelative } from "../components/format";

/**
 * Documents — the hunting team's shared files, in Folders.
 *
 * <p>Distinct from the Forum, which is where the team talks: a Post scrolls
 * away, and an arrende agreement has to be findable next season. This screen is
 * therefore a filing cabinet and not a feed — folders, a breadcrumb, and a list
 * you can tick through.
 *
 * <p>One structural decision worth knowing: the folder tree is fetched whole,
 * once, and navigating between folders re-fetches only the documents. The tree
 * is a few dozen rows for a dozen people, and the breadcrumb needs ancestors
 * the current level does not know about anyway.
 */

/** Matches DocumentService.MAX_FILE_BYTES / MAX_FILES_PER_UPLOAD. */
const MAX_FILE_MB = 25;
const MAX_FILES = 20;
/**
 * Ceiling on the whole upload, not one file.
 *
 * <p>nginx caps the request body at 32m (see docker/prod) and answers an
 * over-sized one with a bare 413 before it ever reaches the API — so there is no
 * message the server could send. Caught here, the person is told which file
 * tipped it over while they can still drop one. Same reasoning as the Forum
 * composer, same number.
 */
const MAX_TOTAL_MB = 30;

function totalMb(files: File[]): number {
  return files.reduce((n, f) => n + f.size, 0) / (1024 * 1024);
}

export function DocumentsScreen() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<TeamDocument[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Which rows are ticked. Ids rather than indices, and cleared on navigation:
   * a selection that survived a folder change would let "Download selected"
   * quietly include things that are no longer on screen.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [moving, setMoving] = useState(false);
  const [confirming, setConfirming] = useState<TeamDocument | null>(null);

  const load = useCallback(async (folderId: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const [tree, docs] = await Promise.all([api.listFolders(), api.listDocuments(folderId)]);
      setFolders(tree);
      setDocuments(docs);
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Documents are for hunting team members. Ask an admin if you should have access."
          : "Could not load the documents. Try again in a moment.",
      );
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(current);
  }, [load, current]);

  const openFolder = useCallback((folderId: string | null) => {
    setSelected(new Set());
    setNotice(null);
    setCurrent(folderId);
  }, []);

  /** Ancestors of the current folder, root first — the breadcrumb. */
  const trail = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.folderId, f]));
    const out: Folder[] = [];
    let id = current;
    // Bounded, not `while (id)`: a cycle cannot be built through the API, and
    // an infinite loop in a breadcrumb would take the whole screen with it.
    for (let i = 0; id && i < 32; i++) {
      const f = byId.get(id);
      if (!f) break;
      out.unshift(f);
      id = f.parentId;
    }
    return out;
  }, [folders, current]);

  const children = useMemo(
    () =>
      folders
        .filter((f) => f.parentId === current)
        .sort((a, b) => a.name.localeCompare(b.name, "sv")),
    [folders, current],
  );

  const allSelected = documents.length > 0 && selected.size === documents.length;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function downloadSelected() {
    if (busy || selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      // One file selected still goes through the bundle endpoint. A ZIP holding
      // one document is mildly silly, but a button that sometimes hands you a
      // PDF and sometimes an archive is worse — you cannot tell what you are
      // about to get before you click it.
      await api.downloadBundle([...selected]);
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 404
          ? "Those documents are no longer here. Reload the page."
          : "Could not build the download.",
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Refile the ticked rows.
   *
   * <p>The moved documents leave this folder, so they are dropped from the list
   * rather than re-fetched — the server has already told us they went. The
   * folder counts in the sidebar do go stale, so the tree is refreshed; that is
   * one small request against showing "3 documents" under a folder that now
   * holds five.
   */
  async function moveSelected(folderId: string | null) {
    setBusy(true);
    setError(null);
    try {
      const moved = await api.moveDocuments([...selected], folderId);
      const gone = new Set(moved.map((d) => d.documentId));
      setDocuments((prev) => prev.filter((d) => !gone.has(d.documentId)));
      setSelected(new Set());
      setMoving(false);
      setFolders(await api.listFolders());
      setNotice(
        moved.length === 1
          ? `Moved “${moved[0].filename}”.`
          : `Moved ${moved.length} documents.`,
      );
    } catch (e) {
      setError(readableError(e) ?? "Could not move those documents.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete(doc: TeamDocument) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteDocument(doc.documentId);
      setDocuments((prev) => prev.filter((d) => d.documentId !== doc.documentId));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(doc.documentId);
        return next;
      });
      setConfirming(null);
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Only the person who uploaded this, or an admin, can delete it."
          : "Could not delete that document.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function deleteFolder(folder: Folder) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteFolder(folder.folderId);
      setFolders((prev) => prev.filter((f) => f.folderId !== folder.folderId));
    } catch (e) {
      // The server's message names what is in the way — "holds 3 documents" —
      // which is more use than anything generic this screen could say.
      setError(readableError(e) ?? "Could not delete that folder.");
    } finally {
      setBusy(false);
    }
  }

  const onUploaded = useCallback((created: TeamDocument[]) => {
    setDocuments((prev) => [...created, ...prev]);
    setNotice(
      created.length === 1
        ? `Uploaded “${created[0].filename}”.`
        : `Uploaded ${created.length} files.`,
    );
  }, []);

  return (
    <div className="docs">
      <div className="sched-hd">
        <div>
          <div className="eyebrow">Hunting team</div>
          <h1 className="sched-title serif">Documents</h1>
        </div>
        <div className="forum-hd-actions">
          <button
            className="chip"
            type="button"
            onClick={() => setCreatingFolder(true)}
            disabled={busy}
          >
            New folder
          </button>
        </div>
      </div>

      <Breadcrumb trail={trail} onOpen={openFolder} />

      <Dropzone folderId={current} onUploaded={onUploaded} onError={setError} disabled={busy} />

      {error && <p className="sched-error">{error}</p>}
      {notice && !error && <p className="docs-notice">{notice}</p>}
      {loading && <p className="audit-empty">Loading…</p>}

      {!loading && (
        <>
          {children.length > 0 && (
            <ul className="docs-folders">
              {children.map((f) => (
                <li key={f.folderId}>
                  <button type="button" className="docs-folder" onClick={() => openFolder(f.folderId)}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M3 7.5a1.5 1.5 0 0 1 1.5-1.5h4l2 2.5h8A1.5 1.5 0 0 1 20 10v7.5a1.5 1.5 0 0 1-1.5 1.5h-14A1.5 1.5 0 0 1 3 17.5z" />
                    </svg>
                    <span className="docs-folder-name">{f.name}</span>
                    <span className="docs-folder-meta">
                      {describeContents(f) || "empty"}
                    </span>
                  </button>
                  {/* Only offered for an empty one. The server refuses the rest
                      with a message, but a button that is always refused is a
                      button that teaches people to distrust the screen. */}
                  {f.documentCount === 0 && f.folderCount === 0 && (
                    <button
                      type="button"
                      className="sched-link sched-link-danger docs-folder-drop"
                      onClick={() => void deleteFolder(f)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {documents.length > 0 && (
            <div className="docs-toolbar">
              <label className="docs-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  // Neither all nor none — the box is neither ticked nor empty.
                  ref={(el) => {
                    if (el) el.indeterminate = selected.size > 0 && !allSelected;
                  }}
                  onChange={() =>
                    setSelected(
                      allSelected ? new Set() : new Set(documents.map((d) => d.documentId)),
                    )
                  }
                />
                <span>
                  {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                </span>
              </label>
              <button
                type="button"
                className="chip"
                disabled={selected.size === 0 || busy}
                onClick={() => setMoving(true)}
              >
                Move to…
              </button>
              <button
                type="button"
                className="chip active"
                disabled={selected.size === 0 || busy}
                onClick={() => void downloadSelected()}
              >
                {busy ? "Preparing…" : "Download selected"}
              </button>
            </div>
          )}

          {documents.length === 0 && children.length === 0 && (
            <div className="empty-day">
              <div className="glyph">Nothing filed here yet.</div>
              <div>Drop a file above — maps, arrende agreements, licence scans.</div>
            </div>
          )}

          {documents.length > 0 && (
            <ul className="docs-list">
              {documents.map((d) => (
                <DocumentRow
                  key={d.documentId}
                  doc={d}
                  checked={selected.has(d.documentId)}
                  onToggle={() => toggle(d.documentId)}
                  onDelete={() => setConfirming(d)}
                  onDriveRetried={(updated) =>
                    setDocuments((prev) =>
                      prev.map((x) => (x.documentId === updated.documentId ? updated : x)),
                    )
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}

      {creatingFolder && (
        <NewFolderDialog
          parentId={current}
          parentName={trail.length > 0 ? trail[trail.length - 1].name : null}
          onClose={() => setCreatingFolder(false)}
          onCreated={(f) => {
            setFolders((prev) => [...prev, f]);
            setCreatingFolder(false);
          }}
        />
      )}

      {moving && (
        <MoveDialog
          folders={folders}
          currentFolderId={current}
          count={selected.size}
          busy={busy}
          onClose={() => setMoving(false)}
          onMove={(target) => void moveSelected(target)}
        />
      )}

      {confirming && (
        <div className="ev-overlay" role="presentation" onClick={() => setConfirming(null)}>
          <div
            className="ev-dialog docs-confirm"
            role="dialog"
            aria-modal="true"
            aria-label="Delete document"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="sched-delete-warn">
              Delete “{confirming.filename}”? This cannot be undone.
              {confirming.driveStatus === "copied" && (
                <> The copy in Google Drive is left alone.</>
              )}
            </p>
            <div className="ev-actions">
              <button type="button" className="chip" onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="chip danger"
                disabled={busy}
                onClick={() => void confirmDelete(confirming)}
              >
                {busy ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Breadcrumb({
  trail,
  onOpen,
}: {
  trail: Folder[];
  onOpen: (id: string | null) => void;
}) {
  return (
    <nav className="docs-crumbs" aria-label="Folder path">
      <button type="button" className="docs-crumb" onClick={() => onOpen(null)}>
        All documents
      </button>
      {trail.map((f, i) => (
        <span key={f.folderId}>
          <span className="docs-crumb-sep" aria-hidden="true">
            /
          </span>
          <button
            type="button"
            className="docs-crumb"
            onClick={() => onOpen(f.folderId)}
            aria-current={i === trail.length - 1 ? "page" : undefined}
          >
            {f.name}
          </button>
        </span>
      ))}
    </nav>
  );
}

/**
 * One Document. The thumbnail replaces the type icon where there is one — for a
 * photograph of a map, the picture is the filename.
 */
function DocumentRow({
  doc,
  checked,
  onToggle,
  onDelete,
  onDriveRetried,
}: {
  doc: TeamDocument;
  checked: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onDriveRetried: (d: TeamDocument) => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const created = new Date(doc.createdAt);

  async function retryDrive() {
    setRetrying(true);
    try {
      onDriveRetried(await api.retryDriveCopy(doc.documentId));
    } catch {
      // The row already says "failed" and still does. A second error line for a
      // retry of a retry is noise on top of news the person already has.
    } finally {
      setRetrying(false);
    }
  }

  return (
    <li className={`docs-row${checked ? " selected" : ""}`}>
      <label className="docs-check docs-row-check">
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <span className="sr-only">Select {doc.filename}</span>
      </label>

      <div className="docs-row-glyph">
        {doc.hasThumbnail ? (
          <img src={api.documentThumbnailUrl(doc.documentId)} alt="" loading="lazy" />
        ) : (
          <FileTypeIcon filename={doc.filename} contentType={doc.contentType} />
        )}
      </div>

      <div className="docs-row-main">
        {/* A plain link, so the browser's own download machinery handles it —
            the server answers with an attachment disposition, so this never
            navigates away from the screen. Middle-click and "save as" work,
            which they would not through an onClick. */}
        <a className="docs-row-name" href={api.documentContentUrl(doc.documentId)} download>
          {doc.filename}
        </a>
        <div className="docs-row-meta">
          {fmtBytes(doc.sizeBytes)} · {doc.uploadedBy} ·{" "}
          <time dateTime={doc.createdAt} title={created.toLocaleString()}>
            {fmtRelative(created)}
          </time>
        </div>
      </div>

      <DriveBadge doc={doc} retrying={retrying} onRetry={() => void retryDrive()} />

      {doc.canDelete && (
        <button
          type="button"
          className="sched-link sched-link-danger docs-row-drop"
          onClick={onDelete}
        >
          Delete
        </button>
      )}
    </li>
  );
}

/**
 * How the Google Drive copy is going.
 *
 * <p>Nothing at all when the mirror is off — a host with no Drive credentials
 * should not show twelve rows of a status that will never change. "Copied" is
 * deliberately quiet: it is the expected state, and a badge on every row would
 * be chrome nobody reads.
 */
function DriveBadge({
  doc,
  retrying,
  onRetry,
}: {
  doc: TeamDocument;
  retrying: boolean;
  onRetry: () => void;
}) {
  if (doc.driveStatus === "off") return null;
  if (doc.driveStatus === "copied") {
    return (
      <span className="docs-drive docs-drive-ok" title="Copied to Google Drive">
        Drive
      </span>
    );
  }
  if (doc.driveStatus === "pending") {
    return (
      <span className="docs-drive" title="Waiting to be copied to Google Drive">
        Drive…
      </span>
    );
  }
  return (
    <button
      type="button"
      className="docs-drive docs-drive-failed"
      onClick={onRetry}
      disabled={retrying}
      title={doc.driveError ?? "The copy to Google Drive failed"}
    >
      {retrying ? "Retrying…" : "Drive failed — retry"}
    </button>
  );
}

/**
 * Drag files onto it, or click it. Both, because both are how people expect to
 * add a file and neither is discoverable enough on its own.
 */
function Dropzone({
  folderId,
  onUploaded,
  onError,
  disabled,
}: {
  folderId: string | null;
  onUploaded: (created: TeamDocument[]) => void;
  onError: (message: string | null) => void;
  disabled: boolean;
}) {
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const send = useCallback(
    async (picked: FileList | null) => {
      if (!picked || picked.length === 0) return;
      const files = Array.from(picked);
      onError(null);

      if (files.length > MAX_FILES) {
        onError(`At most ${MAX_FILES} files at a time.`);
        return;
      }
      const tooBig = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
      if (tooBig) {
        onError(`“${tooBig.name}” is larger than ${MAX_FILE_MB} MB.`);
        return;
      }
      if (totalMb(files) > MAX_TOTAL_MB) {
        onError(
          `That is over ${MAX_TOTAL_MB} MB in total — nginx refuses the request before ` +
            `it reaches us. Upload them in two goes.`,
        );
        return;
      }

      setUploading(true);
      try {
        onUploaded(await api.uploadDocuments(folderId, files));
      } catch (e) {
        onError(
          e instanceof HttpError && e.status === 403
            ? "Uploading is for hunting team members."
            : readableError(e) ?? "Could not upload. Try again in a moment.",
        );
      } finally {
        setUploading(false);
      }
    },
    [folderId, onUploaded, onError],
  );

  return (
    <div
      className={`docs-drop${over ? " over" : ""}${uploading ? " busy" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) void send(e.dataTransfer.files);
      }}
    >
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          void send(e.target.files);
          // Clearing lets the same file be re-picked after a failure; a file
          // input will not re-fire change for an unchanged value.
          e.target.value = "";
        }}
      />
      <button
        type="button"
        className="docs-drop-btn"
        onClick={() => input.current?.click()}
        disabled={disabled || uploading}
      >
        {uploading ? "Uploading…" : "Drop files here, or choose files"}
      </button>
      <p className="ev-invitee-note">
        Any kind of file — up to {MAX_FILES} at a time, {MAX_FILE_MB} MB each, {MAX_TOTAL_MB} MB
        in total.
      </p>
    </div>
  );
}

/**
 * Pick where the ticked documents should go.
 *
 * <p>The whole tree as a flat indented list rather than an expandable one. It is
 * a few dozen folders at the outside, and a picker you have to open twice to
 * find a destination is worse than a slightly long list — the point of this
 * dialog is to be finished with quickly.
 *
 * <p>The folder they are already in is shown but not selectable: offering a
 * move that does nothing is a way of making somebody think they did something.
 */
function MoveDialog({
  folders,
  currentFolderId,
  count,
  busy,
  onClose,
  onMove,
}: {
  folders: Folder[];
  currentFolderId: string | null;
  count: number;
  busy: boolean;
  onClose: () => void;
  onMove: (folderId: string | null) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Depth-first, so a child sits under its parent and the indent means what it
  // looks like it means.
  const ordered = useMemo(() => {
    const byParent = new Map<string | null, Folder[]>();
    for (const f of folders) {
      const list = byParent.get(f.parentId) ?? [];
      list.push(f);
      byParent.set(f.parentId, list);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name, "sv"));
    }
    const out: { folder: Folder; depth: number }[] = [];
    const walk = (parent: string | null, depth: number) => {
      // Bounded, like the breadcrumb: a cycle is not reachable through the API,
      // and an infinite walk here would hang the dialog.
      if (depth > 8) return;
      for (const f of byParent.get(parent) ?? []) {
        out.push({ folder: f, depth });
        walk(f.folderId, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [folders]);

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog docs-move"
        role="dialog"
        aria-modal="true"
        aria-label="Move documents"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ev-dialog-hd">
          <h2 className="serif">
            Move {count === 1 ? "1 document" : `${count} documents`}
          </h2>
          <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <ul className="docs-move-list">
          <li>
            <button
              type="button"
              className="docs-move-target"
              disabled={busy || currentFolderId === null}
              onClick={() => onMove(null)}
            >
              All documents
              {currentFolderId === null && <span className="docs-move-here">already here</span>}
            </button>
          </li>
          {ordered.map(({ folder, depth }) => (
            <li key={folder.folderId}>
              <button
                type="button"
                className="docs-move-target"
                style={{ paddingLeft: `${12 + depth * 18}px` }}
                disabled={busy || folder.folderId === currentFolderId}
                onClick={() => onMove(folder.folderId)}
              >
                {folder.name}
                {folder.folderId === currentFolderId && (
                  <span className="docs-move-here">already here</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <div className="ev-actions">
          <button type="button" className="chip" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function NewFolderDialog({
  parentId,
  parentName,
  onClose,
  onCreated,
}: {
  parentId: string | null;
  parentName: string | null;
  onClose: () => void;
  onCreated: (f: Folder) => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      onCreated(await api.createFolder(name.trim(), parentId));
    } catch (err) {
      // The server names the clash — "there is already a folder called X here".
      setError(readableError(err) ?? "Could not create that folder.");
      setSaving(false);
    }
  }

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="New folder"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="ev-dialog-hd">
            <h2 className="serif">New folder</h2>
            <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="ev-grid">
            <label className="ev-field ev-wide">
              <span>Name</span>
              <input
                ref={field}
                type="text"
                required
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kartor"
              />
            </label>
            <p className="ev-invitee-note ev-wide">
              {parentName ? `Inside “${parentName}”.` : "At the top level."}
            </p>
          </div>

          {error && <p className="ev-error">{error}</p>}

          <div className="ev-actions">
            <button type="button" className="chip" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="chip active" disabled={saving || !name.trim()}>
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** "3 documents · 1 folder", or "" when it is empty. */
function describeContents(f: Folder): string {
  const parts: string[] = [];
  if (f.documentCount > 0) {
    parts.push(f.documentCount === 1 ? "1 document" : `${f.documentCount} documents`);
  }
  if (f.folderCount > 0) {
    parts.push(f.folderCount === 1 ? "1 folder" : `${f.folderCount} folders`);
  }
  return parts.join(" · ");
}

/**
 * The `message` out of an API error body, when there is one. The server answers
 * a 400 with `{code, message}`, and its message names the file or the folder
 * that was wrong — which is the whole value of showing it rather than a generic
 * line. Same helper as `ForumScreen`, kept local for the same reason: they are
 * two screens' worth of error handling, not a shared abstraction.
 */
function readableError(err: unknown): string | null {
  if (!(err instanceof HttpError)) return null;
  try {
    const parsed = JSON.parse(err.body) as { message?: string };
    return typeof parsed.message === "string" && parsed.message ? parsed.message : null;
  } catch {
    return null;
  }
}
